import React, { useState, useRef } from 'react'
import axios from 'axios'
import { Send, Loader2, Sparkles } from 'lucide-react'

/**
 * AgentAsk — a compact "ask this agent anything" box, reusable on every service
 * page and per-agent on the Agents console. POSTs to /api/v1/agent/:service/ask
 * and renders the grounded answer (reply + source chips + confidence + action
 * links). service='chief' asks the holistic Chief; any catalog key asks that
 * agent, framed as itself but grounded across every service's real data.
 */
const arr = (v) => (Array.isArray(v) ? v : [])

export default function AgentAsk({ service = 'chief', agentName = 'the agent', intro, starters = [] }) {
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [thread, setThread] = useState([])
  const boxRef = useRef(null)

  const scrollDown = () => setTimeout(() => { const el = boxRef.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }) }, 60)

  const send = async (text) => {
    const message = String(text ?? input).trim()
    if (!message || busy) return
    setInput('')
    setThread((t) => [...t, { role: 'user', text: message }])
    setBusy(true)
    scrollDown()
    try {
      const { data } = await axios.post(`/api/v1/agent/${encodeURIComponent(service)}/ask`, { message })
      setThread((t) => [...t, {
        role: 'agent',
        text: data?.reply || '…',
        sources: arr(data?.sources),
        confidence: typeof data?.confidence === 'number' ? data.confidence : null,
        links: arr(data?.links),
      }])
    } catch (e) {
      setThread((t) => [...t, { role: 'agent', error: true, text: e?.response?.data?.error || 'I could not answer just now — try again in a moment.' }])
    } finally {
      setBusy(false)
      scrollDown()
    }
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-gray-100 bg-gray-50/70">
        <span className="w-7 h-7 rounded-lg bg-primary-500/10 flex items-center justify-center shrink-0"><Sparkles size={15} className="text-primary-600" /></span>
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-900 leading-tight truncate">Ask {agentName}</p>
          <p className="text-[11px] text-gray-400 leading-tight">{intro || 'Ask anything — answered live from your data.'}</p>
        </div>
      </div>

      {thread.length > 0 && (
        <div ref={boxRef} className="max-h-96 overflow-y-auto px-4 py-3 space-y-3">
          {thread.map((m, i) => m.role === 'user' ? (
            <div key={i} className="flex justify-end">
              <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary-600 text-white text-sm px-3.5 py-2">{m.text}</div>
            </div>
          ) : (
            <div key={i} className="max-w-[94%]">
              <div className={`rounded-2xl rounded-bl-sm text-sm leading-relaxed px-3.5 py-2.5 whitespace-pre-wrap ${m.error ? 'bg-red-500/10 text-red-600' : 'bg-gray-100 text-gray-800'}`}>{m.text}</div>
              {(m.sources?.length > 0 || m.confidence != null) && (
                <div className="flex items-center flex-wrap gap-1.5 mt-1.5 px-1">
                  {arr(m.sources).map((s, si) => <span key={si} className="text-[10px] font-semibold text-primary-600 bg-primary-500/10 rounded px-1.5 py-0.5">{s}</span>)}
                  {m.confidence != null && <span className="text-[10px] text-gray-400 uppercase tracking-wide ml-auto shrink-0">Confidence {Math.round(m.confidence * 100)}%</span>}
                </div>
              )}
              {arr(m.links).some((l) => l && l.to) && (
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 px-1">
                  {arr(m.links).map((l, li) => l && l.to ? (
                    <a key={li} href={l.to} className="text-[11px] font-semibold text-primary-600 hover:underline">{(l.label || 'Open').replace(/^Open /, '')} →</a>
                  ) : null)}
                </div>
              )}
            </div>
          ))}
          {busy && <div className="flex items-center gap-2 text-xs text-gray-400"><Loader2 size={13} className="animate-spin" /> {agentName} is thinking…</div>}
        </div>
      )}

      {thread.length === 0 && starters.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-4 pt-3">
          {starters.map((s, i) => (
            <button key={i} onClick={() => send(s)} className="text-[12px] font-medium text-gray-600 border border-gray-200 rounded-full px-2.5 py-1 hover:border-primary-400 hover:text-primary-600 transition-colors">{s}</button>
          ))}
        </div>
      )}

      <form className="flex items-center gap-2 p-3" onSubmit={(e) => { e.preventDefault(); send() }}>
        <input value={input} onChange={(e) => setInput(e.target.value)} disabled={busy}
          placeholder={`Ask ${agentName} anything…`} className="input text-sm flex-1" />
        <button type="submit" disabled={busy || !input.trim()} aria-label="Send" className="btn-primary p-2.5 disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </form>
    </div>
  )
}
